import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { clone } from './../common/utils';
import handleUnsafeOperationError from './operation-insecure';
import { OrganizationService } from './organization.service';

interface AuthUser {
  adminFlag: boolean;
  id: number;
  isUserSelectedOrganizationActive: boolean;
  organizationId: number;
  role: number;
  viewOnlyFlag: boolean;
}

interface JwtPayload {
  adminFlag: boolean;
  exp: number;
  iat: number;
  id: number;
  organizationId: number;
  role: number;
  viewOnlyFlag: boolean;
}

@Injectable()
export class AuthService {
  public authToken: string = null;
  public refresh: string = null;
  public expires: number = 0;
  public user: AuthUser = null;

  protected readonly authTokenKey: string = '__pCard_t';
  protected readonly expiresKey: string = '__pCard_e';
  protected readonly refreshKey: string = '__pCard_r';
  protected readonly userKey: string = '__pCard_u';

  constructor(
    public  readonly router              : Router,

    private readonly dialog              : MatDialog,
    private readonly organizationService : OrganizationService
  ) { }

  loggedIn(): boolean {
    return Boolean(this.getAuthToken())
      && Boolean(this.getUser());
  }

  setAuthToken(token: string): void {
    try {
      localStorage.setItem(this.authTokenKey, token);
      this.authToken = token;
    } catch {
      this.defaultErrorHandler();
    }
  }

  getAuthToken(): string {
    try {
      if (localStorage.getItem(this.authTokenKey) && localStorage.getItem(this.authTokenKey) !== '') {
        const expires = Number(localStorage.getItem(this.expiresKey));
        const seconds = Math.floor(new Date().getTime() / 1000);

        if (seconds >= expires) {
          this.router.navigate(['/logout']);
          this.logout();
        } else {
          this.setAuthToken(localStorage.getItem(this.authTokenKey));
        }
      } else {
        return '';
      }
    } catch {
      this.defaultErrorHandler();
    }

    return this.authToken;
  }

  setRefresh(refresh: string): void {
    try {
      localStorage.setItem(this.refreshKey, refresh);
      this.refresh = refresh;
    } catch {
      this.defaultErrorHandler();
    }
  }

  getRefresh(): string {
    try {
      if (localStorage.getItem(this.refreshKey) && localStorage.getItem(this.refreshKey) !== '') {
        this.setRefresh(localStorage.getItem(this.refreshKey));
      }
    } catch {
      this.defaultErrorHandler();
    }

    return this.refresh;
  }

  setExpires(expires: number): void {
    try {
      localStorage.setItem(this.expiresKey, String(expires));
      this.expires = expires;
    } catch {
      this.defaultErrorHandler();
    }
  }

  getExpires(): number {
    try {
      if (localStorage.getItem(this.expiresKey) && localStorage.getItem(this.expiresKey) !== '') {
        this.setExpires(Number(localStorage.getItem(this.expiresKey)));
      }
    } catch {
      this.defaultErrorHandler();
    }

    return this.expires;
  }

  setUser(user: AuthUser): void {
    try {
      localStorage.setItem(this.userKey, JSON.stringify(user));
      this.user = user;
    } catch {
      this.defaultErrorHandler();
    }
  }

  getUser(): AuthUser {
    try {
      if (localStorage.getItem(this.userKey)) {
        this.user = JSON.parse(localStorage.getItem(this.userKey));
      }
    } catch {
      this.defaultErrorHandler();
    }

    return clone(this.user);
  }

  getViewOnlyPermission(): boolean {
    const user = this.getUser();

    if (user?.adminFlag || user?.viewOnlyFlag === null)
      return false;

    return user?.viewOnlyFlag ?? true;
  }

  async parseTokenAndSetState(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload: JwtPayload = JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      )
    );

    this.setAuthToken(token);
    this.setExpires(payload.exp);
    this.setUser({
      id: payload.id,
      organizationId: payload.organizationId,
      role: payload.role,
      adminFlag: payload.adminFlag,
      viewOnlyFlag: payload.viewOnlyFlag,
      isUserSelectedOrganizationActive: (await this.organizationService.getRefOrganization()).data?.isActive
    });
  }

  logout() {
    this.setAuthToken(null);
    this.setRefresh(null);
    this.setExpires(null);
    this.setUser(null);

    localStorage.removeItem(this.authTokenKey);
    localStorage.removeItem(this.expiresKey);
    localStorage.removeItem(this.refreshKey);
    localStorage.removeItem(this.userKey);
  }

  defaultErrorHandler(): void {
    handleUnsafeOperationError(this.dialog);
  }
}
